import React, { useState, useEffect, useCallback } from "react";

import PropTypes from "prop-types";
import {
  Typography,
  OutlinedInput,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Button,
  Collapse,
  InputAdornment,
} from "@material-ui/core";
import BlockIcon from "@material-ui/icons/Block";
import CancelIcon from "@material-ui/icons/Cancel";

import { useStyles } from "./orderSummaryForm.styles";
import { LockIcon, ArrowLeftIcon } from "../../../shared/icons";
import Popup from "../../../policies/popup";
import { SuccessPaymentModal } from "../successPaymentModal";
import { Expand } from "./expand.component";

export const OrderSummaryForm = ({
  coupon,
  total,
  subscription,
  subscriptions,
  couponInput,
  promotionCode,
  couponStatus,
  handleSubscription,
  setCouponInput,
  debouncedHandleCouponChange,
  handleSubmitSub,
  isLoading,
  isOpenSuccesPopup,
  handleCloseSuccessPopup,
  redirectTo,
  isEnrollPage,
}) => {
  const classes = useStyles({
    coupon: coupon?.percentOff || coupon?.redeemBy,
  });
  const [expanded, setExpanded] = useState(false);
  const [isOpenPolices, setIsOpenPolices] = useState(false);

  function getDiscount() {
    let discount;
    if (coupon?.percentOff) {
      discount = (total - subscription?.price).toFixed(2);
    }
    return discount;
  }

  const handleExpandClick = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  useEffect(() => {
    handleSubscription(subscription?.name);
  }, [subscription, handleSubscription]);

  useEffect(() => {
    if (coupon?.percentOff || coupon?.redeemBy) {
      handleExpandClick();
    }
  }, [coupon]);

  return (
    <>
      <form className={classes.form} noValidate onSubmit={handleSubmitSub}>
        <Typography className={classes.subtitle} variant="h5" component="div">
          Order Summary
        </Typography>

        <FormControl>
          <FormLabel id="membership-label" className={classes.membership}>
            Membership Type
          </FormLabel>
          <RadioGroup
            aria-labelledby="membership-label"
            defaultValue="female"
            name="radio-buttons-group"
          >
            {subscriptions.map((subs) => (
              <FormControlLabel
                key={subs?.id}
                className={classes.radioButton}
                checked={subscription?.name === subs?.name}
                value={subs?.name}
                onChange={(event) => {
                  handleSubscription(event.target);
                }}
                control={<Radio />}
                label={subs.name}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <FormControl className={classes.subtotal}>
          <FormLabel id="price-label">Subtotal</FormLabel>
          <FormLabel id="amount-label">{`$ ${subscription.price}`}</FormLabel>
        </FormControl>
        {coupon?.name && (
          <FormControl className={classes.cuponDiscount}>
            <FormLabel id="cupon-label">{coupon.name}</FormLabel>
            <FormLabel id="amount-label">
              {coupon?.percentOff
                ? `(-) $${getDiscount()}`
                : `(-) $${coupon?.amountOff}`}
            </FormLabel>
          </FormControl>
        )}
        <FormControl className={classes.total}>
          <FormLabel id="price-label">Monthly Total</FormLabel>
          <FormLabel id="cupon-discount">{`$ ${total}`}</FormLabel>
        </FormControl>
        <Box className={classes.legend}>
          <Typography>Billing starts after your trial period ends</Typography>
        </Box>
        <Box className={classes.cupon}>
          <Typography
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="cupon code"
          >
            I have a coupon code
            <Expand expand={expanded} className={classes.iconDownChevron} />
          </Typography>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <OutlinedInput
              fullWidth
              id="coupon"
              disabled={promotionCode}
              error={!couponStatus && couponInput.length > 1}
              name="coupon"
              onChange={(e) => {
                setCouponInput(e.target.value);
                debouncedHandleCouponChange.callback();
              }}
              value={couponInput?.trim()}
              className={classes.promotionCode}
              inputProps={{
                classes: { input: "" },
              }}
              endAdornment={
                <InputAdornment position="end">
                  {promotionCode && <BlockIcon />}
                  {!couponStatus && couponInput.length > 1 && (
                    <CancelIcon className={classes.iconError} />
                  )}
                </InputAdornment>
              }
            />
          </Collapse>
          {!couponStatus && couponInput.length > 1 && (
            <span className={classes.errorText}>
              The provided coupon is not correct, please verify it.
            </span>
          )}
        </Box>

        <Button
          type="submit"
          disabled={isLoading}
          className={classes.buttonPay}
          endIcon={<ArrowLeftIcon className={classes.arrowRight} />}
        >
          {coupon.fullPromotionCode && isEnrollPage ? "Continue" : "Pay Now"}
        </Button>
      </form>
      <Box className={classes.policy}>
        <Typography>
          By confirming your subscription, you allow Fruition to charge your
          card for this membership payment and agree to Fruition&apos;s{" "}
          <Button
            disableFocusRipple
            disableRipple
            variant="text"
            onClick={() => setIsOpenPolices((state) => !state)}
          >
            Terms and Conditions of Use
          </Button>
          .
        </Typography>
        <Typography>
          To learn more about how Fruition collects uses and protects your
          personal data, please see the{" "}
          <Button
            disableFocusRipple
            disableRipple
            variant="text"
            onClick={() => setIsOpenPolices((state) => !state)}
          >
            Privacy policy
          </Button>
          .
        </Typography>
        <Typography>
          <LockIcon className={classes.iconLock} />
          Safe secure trusted.&nbsp;
          <Typography component="span">No hassle, cancel anytime!</Typography>
        </Typography>
      </Box>
      <Popup
        open={isOpenPolices}
        handleClose={() => setIsOpenPolices((state) => !state)}
      />
      <SuccessPaymentModal
        isModalOpen={isOpenSuccesPopup}
        handleCloseModal={handleCloseSuccessPopup}
        redirectTo={redirectTo}
      />
    </>
  );
};

OrderSummaryForm.propTypes = {
  coupon: PropTypes.shape({
    amountOff: PropTypes.instanceOf(Object),
    created: PropTypes.string,
    currency: PropTypes.instanceOf(Object),
    duration: PropTypes.string,
    priceId: PropTypes.string,
    name: PropTypes.string,
    id: PropTypes.string,
    object: PropTypes.string,
    percentOff: PropTypes.number,
    redeemBy: PropTypes.number,
    fullPromotionCode: PropTypes.bool,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  total: PropTypes.number.isRequired,
  subscription: PropTypes.shape({
    description: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.number,
    priceId: PropTypes.string,
  }).isRequired,
  subscriptions: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      priceId: PropTypes.string,
    })
  ).isRequired,
  couponInput: PropTypes.string.isRequired,
  couponStatus: PropTypes.bool.isRequired,
  promotionCode: PropTypes.string,
  handleSubscription: PropTypes.func.isRequired,
  setCouponInput: PropTypes.func.isRequired,
  debouncedHandleCouponChange: PropTypes.instanceOf(Object).isRequired,
  handleSubmitSub: PropTypes.func.isRequired,
  isOpenSuccesPopup: PropTypes.bool.isRequired,
  handleCloseSuccessPopup: PropTypes.func.isRequired,
  redirectTo: PropTypes.func.isRequired,
  isEnrollPage: PropTypes.bool.isRequired,
};

OrderSummaryForm.defaultProps = {
  promotionCode: "",
};

OrderSummaryForm.displayName = "OrderSummaryForm";
